import { createContext, useContext } from "react";

/**
 * Mirrors GE.API.RetailPricing.Common.Services.UserTablePermission.
 * One entry per table the JWT user has at least one flag set on
 * (resolved from RP_USER_ROLES + RP_ROLE_TABLES). Tables not in the
 * list are either unmanaged (no RP_ROLE_TABLES row at all) or fully
 * revoked for this user.
 */
export interface UserTablePermission {
  tableName: string;
  canSelect: boolean;
  canInsert: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export type TableOp = "Select" | "Insert" | "Update" | "Delete";

export interface PermissionsContextValue {
  /** Raw permission rows from /api/user/permissions. */
  permissions: UserTablePermission[];
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** Non-null when the fetch failed. UI should treat as "no permissions". */
  error: Error | null;
  /**
   * Returns true when the user has the requested op on the given table.
   *
   * Mirrors RP_ROLE_TABLES semantics: a table not present in the
   * permission set means the user has no row in any role for that
   * table — treat as denied. The single exception is unmanaged
   * tables (no RP_ROLE_TABLES row at all), which the BE attribute
   * lets pass through; the FE can't distinguish those from "denied"
   * without an extra round-trip, so it conservatively returns false
   * here. Read-only menu items should fall back to "always show"
   * when their target table is known-unmanaged.
   */
  can: (tableName: string, op: TableOp) => boolean;
  /** Force a re-fetch (e.g., after an admin-side role change in another tab). */
  refresh: () => void;
}

export const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

/**
 * Returns the permission context. If no PermissionsProvider is mounted
 * (unit tests rendering a leaf in isolation, Storybook, ad-hoc usage
 * outside the auth tree) we hand back a permissive fallback: every
 * can() call returns true.
 *
 * Why permissive rather than denying-by-default:
 *   - Production always wraps with the real provider (mounted in
 *     App.tsx inside the authenticated tree). Real grants always apply.
 *   - Component unit tests historically assert behaviour like
 *     "click Add → navigate" or "click Save → mutation fires" without
 *     mocking the auth context. A deny-by-default fallback would
 *     disable every gated button under test and break those expectations.
 *   - The deploy-not-rolled-out failure mode in production is already
 *     handled at the menu level (MERCHIT-3019, disable-not-hide).
 *
 * Tests that specifically want to assert disabled-when-ungranted should
 * wrap with `<PermissionsContext.Provider value={...}>` and supply a
 * `can()` that returns false for the table under exercise.
 */
export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (ctx === undefined) {
    return PERMISSIVE_PERMISSIONS_CONTEXT;
  }
  return ctx;
}

const PERMISSIVE_PERMISSIONS_CONTEXT: PermissionsContextValue = {
  permissions: [],
  isLoading: false,
  error: null,
  can: () => true,
  refresh: () => {},
};
